import React, { useState, useEffect, useCallback, useMemo } from 'react';

import { Col, Label, FormGroup, Row, Button, FormSection, TabsList, TabsTrigger, TabsContent, Tabs, DefaultTable, ButtonIcon, Input } from 'twyne-new.ui';
import { AutocompleteEditorFormsy as AutocompleteEditor } from 'components/AutocompleteEditor';
import AutocompleteInputForm from '@Components/formsy/AutocompleteInput';
import Select from 'components/formsy/Select';
import TwyneURL from 'components/formsy/TwyneURL';
import RevenueForm from './revenue';
import { getDateFormats } from 'utils';
import GoogleAdsForm from './revenue-share/GoogleAds';
import InputForm from '@Components/formsy/Input';
import { useGlobalStore } from '@Stores/global';
import CodeEditor from '@Components/form/CodeEditor';
import { useIntegrationStore } from '../../store';

import { Plus, Trash } from 'lucide-react';

const loadCustomFields = (options, integrationType) => async getFields => {
  const data = await getFields({ parse: true })

  const customFields = []
  for (let { name, key, type, private: _private } of data) {
    if (_private)
      continue

    customFields.push({ value: `${key}`, label: `${name} (Custom Field)` })
    customFields.push({ value: `${key}_trustedform`, label: `${name} (Custom Field) (TrustedForm Cert)` })
    customFields.push({ value: `${key}_leadid`, label: `${name} (Custom Field) (LeadID Cert)` })

    if (['path_impression', 'path_ping_post'].includes(integrationType)) {
      customFields.push({ value: `${key}_consent_text`, label: `${name} (Custom Field) (TCPA Consent Text)` })
    }

    if (type === 'date')
      customFields.push(...getDateFormats(key).map(value => ({ value, label: `${name} (${value.replace(`${key}_`, '')})` })))
  }

  return {
    options: [].concat(options, customFields)
  }
}

const TwyneSyncParams = props => {
  if (props.type !== 'lead_status_sync') return null
  return <>
    <Row>
      <Col size="6">
        <FormGroup>
          <Input type="text" value={'clickid'} disabled />
        </FormGroup>
      </Col>
      <Col size="6">
        <FormGroup>
          <Input type="text" value={'{externalid}'} disabled />
        </FormGroup>
      </Col>
      <Col size="6">
        <FormGroup>
          <Input type="text" value={'conversiontype'} disabled />
        </FormGroup>
      </Col>
      <Col size="6">
        <FormGroup>
          <Input type="text" value={'{conversiontype}'} disabled />
        </FormGroup>
      </Col>
    </Row>
  </>
}

const twyneFields = [
  { label: "Url", attr: "url", size: 12, Component: TwyneURL, required: true },
  { label: "Publisher", attr: "publisher", size: 4, required: true },
  { label: "Source", attr: "source", size: 4, required: true },
  { label: "Campaign", attr: "campaign", size: 4, required: true },
  props => {
    const componentProps = {
      ...props,
      paramsOpts: loadCustomFields(tags[props.type].concat(twyneOptions(props.type)), props.type) || [...registerFields, ...availableTags, ...twyneOptions(props.type)]
    }

    const Component = props.type === 'path_ping_post' || props.vendor === 'twyne_direct_post' ? PathPingPostParameters : Parameters
    return <Component {...componentProps} />
  },
  { label: "Parameters", attr: "parameters-twy-sync", size: 12, Component: TwyneSyncParams },
]

const twyneOptions = type => ['delayed_register', 'register', 'path_impression'].includes(type) ? [{ label: 'PE-Key', value: 'peKey' }] : []

const wishpondFields = [
  { label: "Key", attr: "wishpond_key" },
  { label: "List", attr: "list" }
]

const aweberFields = [
  { label: "Account Id", attr: "account_id" },
  { label: "List Id", attr: "list_id" },
  { label: "Consumer Key", attr: "consumer_key" },
  { label: "Consumer Secret", attr: "consumer_secret" },
  { label: "Token", attr: "token" },
  { label: "Token Secret", attr: "token_secret" }
]

const sendLaneFields = [
  { label: "Api", attr: "api", required: true },
  { label: "Hash", attr: "hash", required: true }
]

const twyneOptoutFields = [
  { label: "Instance ID", attr: "instance", size: 12, required: true },
  { label: "API Key", attr: "key", size: 12, required: true },
  { label: "API Password", attr: "secret", size: 12, required: true }
]

const trustedFormFields = [
  { label: "API Key", attr: "api_key", size: 12, required: true },
  (props) => <Parameters {...props} paramsOpts={loadCustomFields([...registerFields, ...availableTags, { label: 'Offer ID', value: 'offer_id' }])} />
]

const registerFields = [
  { label: 'uSessionId', value: 'usessionid' },
  { label: 'E-mail MD5', value: 'email_md5' },
  { label: 'State', value: 'state' },
  { label: 'Country', value: 'country' },
  { label: 'Phone', value: 'phone' },
  { label: 'Gender', value: 'gender' },
  { label: 'Age', value: 'age' },
  { label: 'Dob', value: 'dob' },
  { label: 'Dob Month', value: 'dob_mm' },
  { label: 'Dob Day', value: 'dob_dd' },
  { label: 'Dob Year', value: 'dob_yyyy' },
  { label: 'Dob Slashes (mm/dd/yyyy)', value: 'dob_slashes_mmddyyyy' },
  { label: 'Dob (yyyyddmm)', value: 'dob_yyyyddmm' },
  { label: 'Dob Dashes (mm_dd_yyyy)', value: 'dob_dashes_mmddyyyy' },
  { label: 'Last Registration', value: 'last_registration' },
  { label: 'Last Registration Month', value: 'last_registration_mm' },
  { label: 'Last Registration Day', value: 'last_registration_dd' },
  { label: 'Last Registration Year', value: 'last_registration_yyyy' },
  { label: 'Last Registration Slashes (mm/dd/yyyy)', value: 'last_registration_slashes_mmddyyyy' },
  { label: 'Last Registration (yyyyddmm)', value: 'last_registration_yyyyddmm' },
  { label: 'Last Registration Dashes (mm_dd_yyyy)', value: 'last_registration_dashes_mmddyyyy' },
  { label: 'Last Registration Slashes (mm/dd/yyyy hh:mm:ss)', value: 'last_registration_slashes_mmddyyyy_hhmmss' },
  { label: 'Last Registration (yyyyddmm  hh:mm:ss)', value: 'last_registration_yyyyddmm_hhmmss' },
  { label: 'Last Registration Dashes (mm_dd_yyyy  hh:mm:ss)', value: 'last_registration_dashes_mmddyyyy_hhmmss' },
  { label: 'Payouttype', value: 'payouttype' },
  { label: 'Conversion Type', value: 'conversiontype' },
  { label: 'External ID', value: 'externalid' },
  { label: 'Referrer', value: 'referrer' },
  { label: 'Device', value: 'device' },
  { label: 'OS', value: 'os' },
  { label: 'Sid1', value: 'sid1' },
  { label: 'Sid2', value: 'sid2' },
  { label: 'Sid3', value: 'sid3' },
  { label: 'Browser', value: 'browser' },
  { label: 'Leadid Cert', value: 'leadid_cert' },
  { label: 'Trustedform Cert', value: 'trustedform_cert' },
  { label: 'Campaign ID', value: 'campaignid' },
  { label: 'Publisher ID', value: 'publisher_id' },
  { label: 'Allocation ID', value: 'allocation_id' },
  { label: 'Path ID', value: 'path_id' },
  { label: 'Browser Agent', value: 'browseragent' },
  { label: 'Duplicated', value: 'duplicated' },
  { label: 'Registration Completed', value: 'registration_completed' },
  { label: 'Email Verification', value: 'email_verification' },
  { label: 'Fraud Verification', value: 'fraud_verification' },
  { label: 'Postal Verification', value: 'postal_verification' },
  { label: 'Phone Verification', value: 'phone_verification' },
  { label: 'Form ID', value: 'form_id' },
  { label: 'Post ID', value: 'post_id' },
  { label: 'Post Target ID', value: 'post_target_id' },
  { label: 'TCPA Consent', value: 'tcpaconsent' },
  { label: 'Registration Consent', value: 'registration_consent' },
  { label: 'User Sweep Incent Claim URL', value: 'user_sweep_incent_claim_url' },
  { label: 'Domain URL', value: 'domain_url' },
]

const availableTags = [
  { label: 'User ID', value: 'userId' },
  { label: 'User Session ID', value: 'userSessionId' },
  { label: 'Site ID', value: 'siteId' },
  { label: 'First Name', value: 'firstName' },
  { label: 'Last Name', value: 'lastName' },
  { label: 'Address', value: 'address' },
  { label: 'Address 2', value: 'address2' },
  { label: 'City', value: 'city' },
  { label: 'Zip', value: 'zip' },
  { label: 'Email', value: 'email' },
  { label: 'IP Address', value: 'ipaddress' },
  { label: 'Static (Custom Mergetag)', value: 'static' }
]

const registrationCreationFields = [
  { label: 'Registration Creation', value: 'registration_creation' },
  { label: 'Registration Creation Month', value: 'registration_creation_mm' },
  { label: 'Registration Creation Day', value: 'registration_creation_dd' },
  { label: 'Registration Creation Year', value: 'registration_creation_yyyy' },
  { label: 'Registration Creation Slashes (mm/dd/yyyy)', value: 'registration_creation_slashes_mmddyyyy' },
  { label: 'Registration Creation (yyyyddmm)', value: 'registration_creation_yyyyddmm' },
  { label: 'Registration Creation Dashes (mm_dd_yyyy)', value: 'registration_creation_dashes_mmddyyyy' },
  { label: 'Registration Creation Slashes (mm/dd/yyyy hh:mm:ss)', value: 'registration_creation_slashes_mmddyyyy_hhmmss' },
  { label: 'Registration Creation (yyyyddmm  hh:mm:ss)', value: 'registration_creation_yyyyddmm_hhmmss' },
  { label: 'Registration Creation Dashes (mm_dd_yyyy  hh:mm:ss)', value: 'registration_creation_dashes_mmddyyyy_hhmmss' },
]

const tags = {
  sweeps_confirmation: [
    { label: 'Sweep ID', value: 'sweepId' },
    { label: 'Sweep Contest ID', value: 'sweepContestId' },
    { label: 'Sweep Title', value: 'sweepTitle' },
    { label: 'Sweep Token', value: 'token' },
    { label: 'Sweep Ticket', value: 'ticket' },
    { label: 'Sweep Token TTL (seconds)', value: 'tokenTTL' },
    { label: 'Sweep Full Confirmation URL', value: 'fullConfirmationlURL' },
    { label: 'Offer ID', value: 'offer_id' },
    ...availableTags,
    ...registerFields

  ],
  sweeps_confirmation_ticket: [
    { label: 'Sweep ID', value: 'sweepId' },
    { label: 'Sweep Contest ID', value: 'sweepContestId' },
    { label: 'Sweep Title', value: 'sweepTitle' },
    { label: 'Sweep Ticket', value: 'ticket' },
    { label: 'Offer ID', value: 'offer_id' },
    ...availableTags,
    ...registerFields
  ],
  unsubscribe: [
    ...availableTags,
    { label: 'Origin', value: 'origin' },
    { label: 'Unsubscribe Date', value: 'date' },
  ],
  optout: [
    ...availableTags,
    { label: 'Origin', value: 'origin' },
    { label: 'Optout Date', value: 'optoutdate' },
  ],
  delayed_register: [
    ...registerFields,
    ...availableTags,
    ...registrationCreationFields,
    { label: 'Offer ID', value: 'offer_id' },
    { label: 'TCPA Consent Text', value: 'tcpaconsent_text' },
    { label: 'TCPA Consent Text (JS Escape)', value: 'tcpaconsent_text_jsescape' },
  ],
  register: [
    ...registerFields,
    ...availableTags,
    ...registrationCreationFields,
    { label: 'Offer ID', value: 'offer_id' },
    { label: 'TCPA Consent Text', value: 'tcpaconsent_text' },
    { label: 'TCPA Consent Text (JS Escape)', value: 'tcpaconsent_text_jsescape' },
  ],
  revenue_share: [
    { label: 'Revenue Transaction PE-Key', value: 'revenue_transaction_pekey' },
    { label: 'Revenue Transaction Cost', value: 'revenue_transaction_value' },
    { label: 'Revenue Transaction Click Date', value: 'revenue_transaction_click_date' },
    { label: 'Revenue Transaction External ID ', value: 'revenue_transaction_external_id' },
    // { label: 'Revenue User Cost', value: 'revenue_user_total' },
    { label: 'Revenue User Session Cost', value: 'revenue_user_session_total' },
    { label: 'Revenue Path Session Cost', value: 'revenue_path_session_total' },
    { label: 'Path ID', value: 'path_id' },
    { label: 'Path Session UUID', value: 'path_session_uuid' },
    { label: 'Path Page IDpath_page_id', value: 'path_page_id' },
    { label: 'Path Slot Position', value: 'path_slot_position' },
    { label: 'Path User Profile ID', value: 'path_user_profile_id' },
    ...registerFields,
    ...availableTags.slice(0,-1),
  ],
  path_impression: [
    { label: 'Page ID', value: 'page_id' },
    { label: 'Slot Position', value: 'slot_position' },
    { label: 'Path Action', value: 'action' },
    { label: 'Completed', value: 'completed' },
    { label: 'isTCPA', value: 'tcpa_isTCPA' },
    { label: 'TCPA Version', value: 'tcpa_version' },
    { label: 'TCPA Fields', value: 'tcpa_field' },
    { label: 'TCPA Answer', value: 'tcpa_answer' },
    { label: 'Session Revenue', value: 'session_revenue' },
    { label: 'Impression Revenue', value: 'impression_revenue' },
    { label: 'Path User Profile ID', value: 'path_user_profile_id' },
    ...registerFields,
    ...availableTags.slice(0, -1),
  ],
  path_ping_post: [
    ...registerFields,
    ...availableTags.slice(0, -1),
  ],
  lead_status_sync: [
    ...registerFields,
    ...availableTags.slice(0, -1),
  ],
}

const HELPERS = [
  "url_param_value()",
  "md5()",
  "lower()",
  "upper()",
  "multiply()",
  "sum()",
  "capitalize()",
]

const contentType = [
  { label: 'application/json', value: 'application/json' },
  { label: 'text/plain', value: 'text/plain' },
  { label: 'multipart/form-data', value: 'multipart/form-data' },
  { label: 'application/x-www-form-urlencoded', value: 'application/x-www-form-urlencoded' },
]

const buildForm = fields => ({ onCustomChange, configuration, type, vendor }) => {
  if (type === 'lead_status_sync' && vendor === 'twyne') fields = fields.filter(field => field.attr === 'url' || field.attr === 'parameters-twy-sync')
  return <Row>
    {fields.map((Field, idx) => {

      if (typeof Field === 'function')
        return <Col key={idx}>
          <Field onCustomChange={onCustomChange} configuration={configuration} type={type} vendor={vendor} />
        </Col>

      return <Col size={Field.size || 6} key={Field.attr}>
        <FormGroup>
          <Label>{Field.label}</Label>
          {Field.Component ?
            <Field.Component name={`_${Field.attr}`} onChange={value => onCustomChange(Field.attr, value)} value={(configuration && configuration[Field.attr])} required={Field.required} type={type} /> :
            <InputForm name={`_${Field.attr}`} onChange={(value) => onCustomChange(Field.attr, value)} value={(configuration && configuration[Field.attr]) || ''} required={Field.required} />
          }
        </FormGroup>
      </Col>
    })}
  </Row>
}

const TwyneOptOutForm = buildForm(twyneOptoutFields)
const TwyneForm = buildForm(twyneFields)
const WishpondForm = buildForm(wishpondFields)
const AweberForm = buildForm(aweberFields)
const SendLaneForm = buildForm(sendLaneFields)
const TrustedForm = buildForm(trustedFormFields)

const parametersToParams = (parameters) => {
  const newParameters = parameters.map(({key, value, static_value }) => {
    return { key, value: value !== 'static' ? `{${value}}` : `${static_value}` }
  })
  return newParameters
}

function AllInboxForm({ onCustomChange, configuration = {} }) {
  const versions = useMemo(() => [{ value: "", label: "Default (v2)" }, { value: "version3", label: "Version 3" }], [])

  return <Row>
    <Col size="6">
      <FormGroup>
        <Label>Key</Label>
        <InputForm name="_aib_key" value={configuration.aib_key || ''}
          onChange={value => onCustomChange("aib_key", value)} required />
      </FormGroup>
    </Col>
    <Col size="6">
      <FormGroup>
        <Label>Source Id</Label>
        <InputForm name="_source_id" value={configuration.source_id || ''}
          onChange={value => onCustomChange("source_id", value)} required />
      </FormGroup>
    </Col>
    <Col size="6">
      <FormGroup>
        <Label>Version</Label>
        <Select name="_version" value={configuration.version || ''} options={versions}
          onChange={option => onCustomChange("version", option?.value)} />
      </FormGroup>
    </Col>
  </Row>
}

function httpFormComponentBuilder(method) {
  return function HTTPForm({ onCustomChange, configuration, type }) {
    const paramsOpts = useMemo(() => {
      let paramsOpts = tags[type] || availableTags
      if (type !== 'unsubscribe' && type !== 'optout') {
        paramsOpts = loadCustomFields([...paramsOpts, ...['delayed_register', 'register', 'path_impression'].includes(type) ? [{ label: 'PE-Key', value: 'peKey' }] : []], type)
      }
      return paramsOpts
    }, [type])

    const mergetags = useMergeTags({ paramsOpts })

    return <Row>
      <Col size="12">
        <FormGroup>
          <Label>URL</Label>
          {['revenue_share'].includes(type) ? <AutocompleteInputForm
            className="form-control"
            name="_url"
            trigger={["{"]}
            options={{ "{": [...mergetags, ...HELPERS] }}
            value={(configuration && configuration["url"]) || ''}
            onChange={value => onCustomChange("url", value)}
            maxOptions={0}
            required
          /> : <InputForm
            name="_url"
            value={(configuration && configuration["url"]) || ''}
            onChange={value => onCustomChange("url", value)}
            required
          />}
        </FormGroup>
      </Col>
      {
        method === 'post' && ['delayed_register', 'register', 'revenue_share', 'path_impression', 'lead_status_sync'].includes(type) && <ContentType
          onCustomChange={onCustomChange}
          configuration={configuration}
        />
      }
      {
        ['delayed_register', 'register', 'revenue_share', 'path_impression', 'lead_status_sync'].includes(type) && <Content
          method={method}
          onCustomChange={onCustomChange}
          configuration={configuration}
          mergetags={mergetags}
        />
      }
      {
        !['delayed_register', 'register', 'revenue_share', 'path_impression', 'lead_status_sync'].includes(type) && <Parameters
          onCustomChange={onCustomChange}
          configuration={configuration}
          paramsOpts={paramsOpts}
          type={type}
        />
      }
    </Row>
  }
}

function ContentType ({ onCustomChange, configuration }) {
  useEffect(() => {
    const { parameters, data } = configuration
    if (parameters && !data)
      onCustomChange('content_type', 'application/x-www-form-urlencoded')
  }, [])

  const handleCustomChange = option => {
    const { value } = option
    onCustomChange("content_type", value)
    onCustomChange('data', null)
    onCustomChange('params', null)
  }

  return <Col size="12">
    <FormGroup>
      <Label>Content Type</Label>
      <Select
        name='_content_type'
        value={configuration.content_type || null}
        options={contentType}
        onChange={handleCustomChange}
        clearable={false}
        required
      />
    </FormGroup>
  </Col>
}

function useMergeTags({ paramsOpts }) {
  const [mergetags, setMergetags] = useState([])
  const { customFields: { getFields } } = useGlobalStore()

  const loadMergeTags = useCallback(async () => {
    if (typeof paramsOpts === "function") {
      const paramsOptions = await paramsOpts(getFields)
      setMergetags(paramsOptions.options.map(option => `${option.value}`))
    } else {
      setMergetags(paramsOpts.map(option => `${option.value}`))
    }
  }, [paramsOpts, getFields])

  useEffect(() => {
    loadMergeTags()
  }, [loadMergeTags])

  return mergetags
}

function Content({ method, onCustomChange, configuration, mergetags }) {
  const { content_type } = configuration

  const isStructured = () => [
    'multipart/form-data', 'application/x-www-form-urlencoded'
  ].includes(content_type)

  const isText = () => [
    'application/json', 'text/plain'
  ].includes(content_type)

  if (method === "get" || isStructured()) {
    return <>
      <Col size='12'>
        <Params
          method={method}
          configuration={configuration}
          onCustomChange={onCustomChange}
          mergetags={mergetags}
        />
      </Col>
    </>
  }

  if (isText()) {
    return <Col size="12">
      <FormGroup>
        <Label>Body</Label>
        <Text
          configuration={configuration}
          onCustomChange={onCustomChange}
          mergetags={mergetags}
        />
      </FormGroup>
    </Col>
  }

  return null
}

function Text ({ configuration, onCustomChange, mergetags }) {
  const { content_type, data } = configuration
  const mode = content_type === 'application/json' ? 'json' : 'plain_text'
  const [text, setText] = useState('')

  useEffect(() => {
    setText('')
  }, [content_type])

  useEffect(() => {
    if (typeof data === 'string')
      setText(data)
  }, [])

  const update = (value) => {
    setText(value)
    onCustomChange('data', value)
  }

  return <AutocompleteEditor
    name="_body_editor"
    key={`${mode}-${mergetags.length}`}
    mode={mode}
    keywords={[...mergetags, ...HELPERS]}
    meta='mergetags'
    value={text}
    onChange={value => update(value)}
    editorOptions={{
      useWorker: mode === 'json',
    }}
    validationErrors={{
      jsonSyntax: "Syntax error, check editor",
    }}
    validations={{
      jsonSyntax: (_, value) => {
        if (mode !== 'json') return true
        try {
          JSON.parse(value)
          return true
        } catch (error) {
          return false
        }
      },
    }}
  />
}

function Params ({ method, configuration, onCustomChange, mergetags }) {
  const { parameters, params, data, content_type } = configuration
  const [fields, setFields] = useState([{ key: '', value: '' }])

  useEffect(() => {
    setFields([{ key: '', value: '' }])
  }, [content_type])

  useEffect(() => {
    if (parameters && !(data || params))
      update(parametersToParams(parameters))

    if (method === 'get' && params)
      setFields(params)

    if (method === 'post' && (data || params) && (typeof data === 'object' || params))
      update(!params ? data : params)
  },[])

  const update = (value) => {
    setFields(value)
    onCustomChange('params', value)
  }

  const handleAdd = () => {
    const tempFields = [...fields]
    tempFields.push({ key: null, value: null })
    setFields(tempFields)
  }

  const handleDel = (index) => {
    const tempFields = [...fields]
    tempFields.splice(index, 1)
    update(tempFields)
  }

  const handleChange = (key, value, index) => {
    const tempFields = [...fields]
    tempFields[index][key] = value
    update(tempFields)
  }

  const paramsTableColumns = [
    {
      dataField: 'key',
      text: 'Key',
      formatter: (cell, row, index) => (
        <FormGroup>
          <InputForm
            name={`param-${index}`}
            type="text"
            value={row.key || ''}
            onChange={value => handleChange('key', value, index)}
            required
          />
        </FormGroup>
      ),
    },
    {
      dataField: 'value',
      text: 'Value',
      formatter: (cell, row, index) => (
        <FormGroup>
          <AutocompleteInputForm
            className="form-control"
            name={`param-value-${index}`}
            trigger={["{"]}
            options={{ "{": [...mergetags, ...HELPERS] }}
            value={row.value || ''}
            onChange={value => handleChange('value', value, index)}
            maxOptions={0}
            required
          />
        </FormGroup>
      ),
    },
    {
      dataField: '_actions',
      text: ' ',
      className: 'w-8',
      formatter: (_, __, index) => <ButtonIcon content='Delete' onClick={() => handleDel(index)}>
        <Trash size="16" />
      </ButtonIcon>,
    },
  ]

  return <>
    <Row className='mt-4'>
      <Col size="12">
        <div className="float-right">
          <Button color="accent" size='sm' onClick={() => handleAdd()}>
            Add
            <Plus size={14} />
          </Button>
        </div>
        <FormSection>
          {method === 'get' ? 'Parameters' : 'Body'}
        </FormSection>
      </Col>
    </Row>
    <DefaultTable
      data={fields}
      columns={paramsTableColumns}
      bordered
      className="mt-2 max-h-[30vh] overflow-y-auto"
    />
  </>
}

function PathPingPostParameters({ ...props }) {
  return <>
    <OverridePingPostMergetags {...props} />
    <Tabs defaultValue={CONFIGS_PATH_PING_POST[0].configKey} size='sm' forceMount>
      <TabsList>
        {CONFIGS_PATH_PING_POST.map(({ configKey, configTitle }) => <TabsTrigger key={configKey} value={configKey}>{configTitle}</TabsTrigger>)}
      </TabsList>
      {CONFIGS_PATH_PING_POST.map(({ configKey }) => <>
        <TabsContent key={configKey} value={configKey}>
          <Parameters {...props} configKey={configKey} configTitle={''} />
        </TabsContent>
      </>)}
    </Tabs>
  </>
}

function OverridePingPostMergetags({ type, configuration, onCustomChange }) {
  const { overrideMergetags, mode } = configuration || {}
  let mergetags = overrideMergetags || {}

  const handleChange = (key, value) => {
    mergetags[key] = value

    if (!value) {
      delete mergetags[key]
    }

    if (Object.keys(mergetags).length === 0) {
      mergetags = null
    }

    onCustomChange('overrideMergetags', mergetags)
  }

  if (type !== 'path_ping_post' || mode === 'only_post') {
    return null;
  }

  return <Row>
    {['tcpaconsent', 'dba'].map(key => <Col size="12" key={key}>
      <FormGroup>
        <Label>{key}</Label>
        <CodeEditor
          name={`ping-post-tag-${key}`}
          value={mergetags[key]}
          onChange={value => handleChange(key, value)}
          className='h-[10vh]!'
        />
      </FormGroup>
    </Col>)}
  </Row>
}

function Parameters({ onCustomChange, configuration, paramsOpts, type, configKey, configTitle }) {

  if (!configKey && !configTitle) {
    configKey = 'parameters'
    configTitle = 'Parameters'
  }

  if (!configuration[configKey])
    configuration[configKey] = []

  const handleAdd = () => {
    handleChange("key", "", -1)
  }

  const handleChange = (key, value, idx) => {
    let configs = configuration[configKey] || [];

    if (idx === -1) //means that is including a new line
      configs.push({ key: null, value: null, static_value: null })
    else if (idx >= 0 && value === -1) //remove
      configs.splice(idx, 1);
    else if (idx >= 0) //update field
      configs[idx][key] = value
    else
      configs[idx][key] = value

    onCustomChange(configKey, configs)
  }

  const validations = {
    callback: {
      customFieldValid: (_, value) => !value || type !== 'path_ping_post' || configKey !== 'customQuestions' ? true : /^cq\d+$/.test(value),
    },
    message: {
      customFieldValid: `Please enter a valid code in the format 'cq' followed by a number (e.g., cq1, cq2, cq3)`
    },
  }

  const tableRows = configuration[configKey] || []
  const parametersColumns = [
    {
      dataField: 'key',
      text: 'Key',
      formatter: (cell, row, index) => (
        <FormGroup>
          <InputForm
            name={`param-${row.key}-${index}`}
            type="text"
            value={row.key || ''}
            onChange={value => handleChange('key', value, index)}
            required
            validations={validations.callback}
            validationErrors={validations.message}
          />
        </FormGroup>
      ),
    },
    {
      dataField: 'value',
      text: 'Value',
      formatter: (cell, row, index) => (
        <FormGroup>
          <Value
            name={`option-${row.key}-${index}`}
            idx={index}
            item={row}
            paramsOpts={paramsOpts}
            handleChange={handleChange}
          />
        </FormGroup>
      ),
    },
    {
      dataField: '_actions',
      text: ' ',
      className: 'w-8',
      formatter: (_, __, index) => (
        <ButtonIcon content='Delete' onClick={() => handleChange('key', -1, index)}>
          <Trash size="16" />
        </ButtonIcon>
      ),
    },
  ]

  return <Col>
    <Row>
      <Col size="12">
        <div className="float-right">
          <Button color="accent" size="sm" onClick={() => handleAdd()}>
            Add
            <Plus size={14} />
          </Button>
        </div>
        <FormSection>
          {configTitle || <>&nbsp;</>}
        </FormSection>
      </Col>
    </Row>
    <DefaultTable
      data={tableRows}
      columns={parametersColumns}
      bordered
      className="mt-2 max-h-[30vh] overflow-y-auto"
      noDataIndication={() => ' '}
    />
  </Col>
}

function Value({ item, handleChange, paramsOpts, idx, name }) {
  const { customFields: { getFields } } = useGlobalStore()
  const { value } = item
  const [mergetags, setMergetags] = useState([])

  useEffect(() => {
    (async () => {
      const paramsOptions = typeof paramsOpts === 'function' ? (await paramsOpts(getFields)).options : paramsOpts
      if (value !== 'static')
        setMergetags(paramsOptions.filter(option => option.label ))
      else
        setMergetags(paramsOptions.map(option => `${option.value}`))
    })()
  },[value])

  if (value !== 'static')
    return <Select
      name={name}
      onChange={(opt) => handleChange('value', opt && opt.value || null, idx) }
      value={item.value} options={mergetags}
      required
    />

  return <AutocompleteInputForm
    name={name}
    trigger={["{"]}
    options={{"{": [...mergetags, ...HELPERS] }}
    value={item.static_value || ''}
    onChange={(value) => handleChange('static_value', value, idx)}
    maxOptions={0}
    required
  />
}

function RewardProgressionForm({ configuration, onCustomChange, type }) {
  const { lists: { rewards: { items: rewards, useLoad: useLoadRewards }, rewardSteps: { items: rewardSteps, useLoad: useRewardStepLoad } } } = useIntegrationStore()

  const params = useMemo(() => ({ rewardId: configuration.rewardId, mode: "progression" }), [configuration.rewardId])

  useRewardStepLoad(params)
  useLoadRewards(params)

  const rewardsOptions = useMemo(() => rewards.map(({ id, name }) => ({ value: id, label: `[#${id}] - ${name}` })), [rewards])
  const rewardsStepsOptions = useMemo(() => rewardSteps.map(({ id, name }) => ({ value: id, label: `[#${id}] - ${name}` })), [rewardSteps])

  const handleChangeReward = option => {
    onCustomChange('rewardId', option ? option.value : null)
    !option && onCustomChange('rewardStepId', null)
  }

  return <Row>
    <Col size="6">
      <FormGroup>
        <Label>Reward</Label>
        <Select name='rewardId' value={configuration.rewardId || ''} onChange={handleChangeReward} options={rewardsOptions} required />
      </FormGroup>
    </Col>
    {configuration.rewardId && <Col size="6">
      <FormGroup>
        <Label>Reward Step</Label>
        <Select name='rewardStepId' value={configuration.rewardStepId || ''} onChange={option => onCustomChange('rewardStepId', option ? option.value : null)} options={rewardsStepsOptions} required />
      </FormGroup>
    </Col>}
  </Row>
}

export default {
  default: RevenueForm,
  midpath: RevenueForm,
  tcpa: RevenueForm,
  registration: RevenueForm,
  delayed_registration: RevenueForm,
  email: RevenueForm,
  pushnotification: RevenueForm,
  retargeting: RevenueForm,
  callcenter: RevenueForm,
  twyne_registration: RevenueForm,
  clickwall: RevenueForm,
  exit: RevenueForm,
  adsense: RevenueForm,
  cmspage: RevenueForm,
  sms: RevenueForm,
  trusted_form: TrustedForm,
  trusted_form_extend: TrustedForm,
  twyne: TwyneForm,
  twyne_direct_post: TwyneForm,
  wishpond: WishpondForm,
  aweber: AweberForm,
  aib: AllInboxForm,
  sendlane: SendLaneForm,
  twyne_optout: TwyneOptOutForm,
  http_get: httpFormComponentBuilder('get'),
  http_post: httpFormComponentBuilder('post'),
  google_ads: GoogleAdsForm,
  path_ping_post: TwyneForm,
  reward_progression: RewardProgressionForm,
}

const CONFIGS_PATH_PING_POST = [
  { configKey: 'customQuestions', configTitle: 'Custom Questions' },
  { configKey: 'customFields', configTitle: 'Custom Fields' },
  { configKey: 'click', configTitle: 'Click' },
]
